import { deleteCookie, getCookie, setBrowserCookie } from '@/utils/cookies';

/** Matches legacy respondent.io cookie names for cross-app compatibility. */
export const REFERRAL_CODE_COOKIE_NAME = 'respondent.referralCode';
export const REFERRAL_PROJECT_ID_COOKIE_NAME = 'respondent.projectId';

// 5 days
const MAX_AGE_SECONDS = 5 * 24 * 60 * 60;

export type SignupReferralSearch = {
  r?: string | undefined;
  referralCode?: string | undefined;
  projectId?: string | undefined;
};

/**
 * Project pages set cookies when `?referralCode=` is present so login/signup detours keep context.
 * Signup resolves **URL params first**, then cookies — one place for email body + OAuth query string.
 */
export function persistProjectReferralAttribution(params: {
  referralCode: string;
  projectId?: string;
}): void {
  const referralCode = params.referralCode.trim();
  if (!referralCode) {
    return;
  }

  setBrowserCookie(REFERRAL_CODE_COOKIE_NAME, referralCode, {
    maxAgeSeconds: MAX_AGE_SECONDS,
  });

  const projectId = params.projectId?.trim();
  if (projectId) {
    setBrowserCookie(REFERRAL_PROJECT_ID_COOKIE_NAME, projectId, {
      maxAgeSeconds: MAX_AGE_SECONDS,
    });
  }
}

function readReferralAttributionCookies(): {
  referralCode?: string;
  projectId?: string;
} {
  const referralCode = getCookie(REFERRAL_CODE_COOKIE_NAME);
  const projectId = getCookie(REFERRAL_PROJECT_ID_COOKIE_NAME);
  return {
    ...(referralCode ? { referralCode } : {}),
    ...(projectId ? { projectId } : {}),
  };
}

/** For signup POST body and `/auth/*` params. Search wins over cookies. */
export function resolveSignupReferralAttribution(
  search: SignupReferralSearch,
): {
  referralCode?: string;
  referrerProjectId?: string;
} {
  const cookie = readReferralAttributionCookies();

  const searchCode =
    search.referralCode?.trim() || search.r?.trim() || undefined;
  const searchProjectId = search.projectId?.trim() || undefined;
  const cookieCode = cookie.referralCode?.trim() || undefined;
  const cookieProjectId = cookie.projectId?.trim() || undefined;

  const hasSearchAttribution =
    searchCode !== undefined || searchProjectId !== undefined;
  const code = hasSearchAttribution ? searchCode : cookieCode;
  const projectId = hasSearchAttribution ? searchProjectId : cookieProjectId;

  return {
    ...(code ? { referralCode: code } : {}),
    ...(projectId ? { referrerProjectId: projectId } : {}),
  };
}

export function clearProjectReferralAttribution(): void {
  deleteCookie(REFERRAL_CODE_COOKIE_NAME);
  deleteCookie(REFERRAL_PROJECT_ID_COOKIE_NAME);
}
